// imagepaste overlay の配置まわりの純関数 (ハンドルの viewport clamp / クラスタ配置の衝突回避)。
// DOM / React に依存しない。測定値 (cluster 寸法) を入力にしてもここでは state を持たない。

import type { Corner } from './imagepasteResize.js';

export interface Pt {
  readonly x: number;
  readonly y: number;
}
export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}
export interface ClientRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** ハンドルの見た目 (px) と当たり判定 (px)。タッチで押せるよう hit は見た目より大きい。 */
export const HANDLE_VISUAL_PX = 14;
export const HANDLE_HIT_PX = 28;
/** クラスタとプレビュー辺の間隔: ハンドル hit の半分 + 余白 (= どの角 hit にも重ならない)。 */
export const CLUSTER_GAP_PX = HANDLE_HIT_PX / 2 + 6;

export const CORNER_KEYS: readonly Corner[] = ['nw', 'ne', 'sw', 'se'];

export interface HandlePoint extends Pt {
  /** viewport 内へ寄せられた (= 実際の角とズレている) */
  readonly clamped: boolean;
}
export type HandlePoints = Record<Corner, HandlePoint>;

/** ハンドル中心を、hit 全体が viewport に収まるよう clamp する。 */
export const clampHandleCenter = (
  pt: Pt,
  viewport: ViewportSize,
  hit: number = HANDLE_HIT_PX,
): HandlePoint => {
  const half = hit / 2;
  const clampAxis = (v: number, size: number): number => {
    if (size <= hit) return size / 2;
    return Math.max(half, Math.min(v, size - half));
  };
  const x = clampAxis(pt.x, viewport.width);
  const y = clampAxis(pt.y, viewport.height);
  return { x, y, clamped: x !== pt.x || y !== pt.y };
};

export const clampHandles = (
  corners: Record<Corner, Pt>,
  viewport: ViewportSize,
): HandlePoints => ({
  nw: clampHandleCenter(corners.nw, viewport),
  ne: clampHandleCenter(corners.ne, viewport),
  sw: clampHandleCenter(corners.sw, viewport),
  se: clampHandleCenter(corners.se, viewport),
});

export const handlesEqual = (a: HandlePoints, b: HandlePoints): boolean =>
  CORNER_KEYS.every((c) => a[c].x === b[c].x && a[c].y === b[c].y && a[c].clamped === b[c].clamped);

/** ハンドル hit (中心 ± hit/2) と矩形が交差するか (接するだけは交差しない)。 */
export const intersectsHandleHit = (
  rect: ClientRect,
  center: Pt,
  hit: number = HANDLE_HIT_PX,
): boolean => {
  const half = hit / 2;
  return (
    rect.left < center.x + half &&
    rect.left + rect.width > center.x - half &&
    rect.top < center.y + half &&
    rect.top + rect.height > center.y - half
  );
};
